import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Container } from '../styles/components';
import SectionNumber from './SectionNumber';

const OverviewSection = styled.section`
  height: calc(100svh - 100px);
  padding: 100px 0;
  position: relative;
  background: ${props => props.theme.colors.charcoal};
  display: flex;
  align-items: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/assets/patterns/Contours - clipped (dark).svg');
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    opacity: 1;
    z-index: 1;
  }
`;

const OverviewContainer = styled(Container)`
  max-width: ${props => props.theme.spacing.maxWidth};
  margin: 0 auto;
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxl};
  align-items: center;
  flex: 1;
  min-height: 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
  justify-content: center;
`;

const OverviewHeading = styled.h2`
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-weight: ${props => props.theme.typography.heading.fontWeight};
  font-size: 2.5rem;
  line-height: 1.1;
  color: ${props => props.theme.colors.bone};
  margin: 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const OverviewHighlight = styled.p`
  font-family: ${props => props.theme.typography.highlight.fontFamily};
  font-weight: ${props => props.theme.typography.highlight.fontWeight};
  font-style: ${props => props.theme.typography.highlight.fontStyle};
  font-size: 2.5rem;
  line-height: 1.3;
  color: ${props => props.theme.colors.terracotta};
  margin: 0;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const OverviewText = styled.p`
  line-height: 1.6;
  color: ${props => props.theme.colors.bone};
  font-weight: 400;
  font-size: ${props => props.theme.typography.sizes.body};
  margin: 0;
  font-family: ${props => props.theme.typography.body.fontFamily};
`;

const Callout = styled.p`
  font-family: ${props => props.theme.typography.highlight.fontFamily};
  font-weight: ${props => props.theme.typography.highlight.fontWeight};
  font-style: ${props => props.theme.typography.highlight.fontStyle};
  font-size: ${props => props.theme.typography.sizes.bodyLarge};
  line-height: 1.3;
  color: ${props => props.theme.colors.gold};
  margin: ${props => props.theme.spacing.md} 0 0 0;
`;

const DiagramColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  min-height: 0;
  justify-content: center;
`;

const LayerImage = styled.img`
  max-width: 400px;
  object-fit: contain;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transform: translateY(${props => props.$isVisible ? '0' : '30px'});
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
  transition-delay: ${props => props.$delay || '0s'};
`;

const MethodologyOverview = () => {
  const [isVisible, setIsVisible] = useState(false);
  const overviewRef = useRef(null);

  useEffect(() => {
    const currentRef = overviewRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.3,
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <OverviewSection id="methodology-overview" ref={overviewRef}>
      <OverviewContainer>
        <TwoColumnLayout>
          <LeftColumn>
            <div>
              <OverviewHeading>
                Everyone else starts with slides.
              </OverviewHeading>
              <OverviewHighlight>
                We'll start with... tortilla chips
              </OverviewHighlight>
            </div>

            <TextGroup>
              <OverviewText>
                Like the perfect plate of nachos, a fundable pitch is built across three layers: <br></br>1. Strategy, 2. Persuasion and 3. Communication.
              </OverviewText>
              <OverviewText>
                Most people jump straight to Layer 2 or 3. But without Layer 1, everything else is just mush.
              </OverviewText>
              <OverviewText>
                We'll create the 'Perfect Nachos' pitch deck. Layer by layer, so you get clarity, consistency and a compelling narrative - fast.
              </OverviewText>
              <Callout>
                We'll prepare you for the raise, not just the presentation.
              </Callout>
            </TextGroup>
          </LeftColumn>

          <DiagramColumn>
            <LayerImage
              src="/assets/communication.png"
              alt="Layer 3: Communication"
              $isVisible={isVisible}
              $delay="0.8s"
            />
            <LayerImage
              src="/assets/sauce.png"
              alt="Layer 2: Persuasion"
              $isVisible={isVisible}
              $delay="0.4s"
            />
            <LayerImage
              src="/assets/nachos-layer-1-chips.png"
              alt="Layer 1: Strategy"
              $isVisible={isVisible}
              $delay="0s"
            />
          </DiagramColumn>
        </TwoColumnLayout>
      </OverviewContainer>
      <SectionNumber number={4} />
    </OverviewSection>
  );
};

export default MethodologyOverview;
